import { firstValueFrom, map } from 'rxjs';
import { dispatchAction } from '@ardoq/rxbeach';
import { logError } from '@ardoq/logging';
import { alert } from '@ardoq/modal';
import {
  ArdoqError,
  translateApiErrorWithTraceId,
} from '@ardoq/common-helpers';
import {
  clearAndSetLoadedState,
  loadedStateUpdated,
  registerLoadedState,
} from 'loadedState/actions';
import { buildTraversalState, isError } from 'loadedState/buildState';
import { TraversalErrorReason } from 'loadedState/traversalTypes';
import { setActiveMainTabLeft } from '../streams/views/mainContent/actions';
import {
  ExecuteSavedViewpointPayload,
  ExecuteViewpointPayload,
} from './actions';
import {
  getSupportedTraversalViewIdOrDefault,
  toRenderedViewpointViewId,
} from 'views/environmentals/getViewpointModeSupportedViews';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import {
  applyFormattingAndLabelRules,
  applyGroupingRules,
} from 'traversals/applyViewpointPerspectives';
import { applyViewpointViewSettings } from 'traversals/applyViewpointViewSettings';
import { requestShowAppModule } from 'appContainer/actions';
import { AppModules } from 'appContainer/types';
import { selectedModule$ } from 'appContainer/selectedModule$';
import { enterViewpointMode } from 'streams/traversals/actions';
import { isExecuteSavedViewpointPayload } from './utils';
import {
  APIViewpointAttributes,
  ComponentSelection,
  GroupingRule,
  LoadedState,
  QueryBuilderQuery,
  ViewpointSavedViewSettings,
  viewpointToTraversalBase,
} from '@ardoq/api-types';
import { scopeDataLoaded } from '../traversals/stagedLoadedDataAndState$';
import {
  setIsLoading,
  setIsLoadingWithConfig,
} from 'components/GlobalPaneLoader/globalPaneLoader$';
import { loadedStateOperations } from '../loadedState/loadedStateOperations';
import { viewpointLoadingStatusChanged } from 'canvas/contextToolBar/loadedStateHistory/loadedStateHistoryActions';
import { Features } from '@ardoq/features';
import { setCanvasSidebarQuery } from 'canvas/sidebarPanel/sidebars/contextSwitcher/actions';
import { getTypeQuery } from 'canvas/utils/getTypeQuery';
import { isDiscoverJourney } from 'canvas/isDiscoverJourney';
import { contextToolBarCommands } from 'canvas/contextToolBar/contextToolBarCommands';
import { componentGraphSelectionChanged } from '@ardoq/component-selection';
import { hasFeature } from 'modelInterface/features/featureInterface';
import { setApplicationViewState } from 'canvas/contextToolBar/loadedStateHistory/setApplicationViewState';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t } from '@ardoq/i18n';
import { api } from '@ardoq/api';

const isLoadedStateHashString = (
  loadedStateHash?: string
): loadedStateHash is string => Boolean(loadedStateHash);

const getComponentSelection = (
  startContext: string[] | QueryBuilderQuery
): ComponentSelection => {
  if (Array.isArray(startContext)) {
    return {
      startSet: startContext,
    };
  }
  return {
    startSet: [],
    startQuery: startContext,
  };
};

type OpenViewpointArgs = {
  viewpoint: APIViewpointAttributes;
  startContext: string[] | QueryBuilderQuery;
  isFirstOpenedTraversal: boolean;
  clearExistingData?: boolean;
  context?: ExecuteSavedViewpointPayload['context'];
  /**
   * Per-view setting overrides merged on top of `viewpoint.viewSettings`
   * before the standalone view receives them. Keyed by `ViewIds`, with
   * each value being a partial view settings object whose keys win over
   * the viewpoint's stored values for the same view. Use this when a
   * caller (e.g. an embed) needs the opened standalone view to inherit
   * the embed's runtime tweaks (layout orientation, collapsed groups,
   * etc.) rather than the viewpoint's persisted defaults.
   */
  viewSettingsOverrides?: ViewpointSavedViewSettings;
  /**
   * Grouping rules that replace `viewpoint.groupBys` before downstream
   * traversal/grouping code runs. Unlike `viewSettingsOverrides`, this is
   * a full replacement (not a merge) because grouping is an ordered list
   * with no obvious merge semantics. Use this when a caller (e.g. an
   * embed) needs the opened standalone view to inherit the embed's
   * runtime grouping (e.g. parent-all) rather than the viewpoint's
   * persisted grouping (typically workspace).
   */
  groupBysOverride?: GroupingRule[];
};

const showTraversalExecutionError = (error: ArdoqError) => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders alert() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  if (api.isForbidden(error)) {
    alert({
      title: t(i18nLocale, 'viewpointBuilder:routine.error.title', 'Error'),
      subtitle: t(
        i18nLocale,
        'viewpointBuilder:routine.error.execute.forbidden.subtitle',
        'Access denied'
      ),
      text: t(
        i18nLocale,
        'viewpointBuilder:routine.error.execute.forbidden.text',
        "You don't have permission to view the components in this viewpoint. Contact your organization administrator."
      ),
    });
    return;
  }
  const { userMessage, supportMessage } = translateApiErrorWithTraceId(
    error,
    i18nLocale,
    t(
      i18nLocale,
      'viewpointBuilder:routine.error.execute.fallback',
      'Failed to load the viewpoint.'
    )
  );
  alert({
    title: t(i18nLocale, 'viewpointBuilder:routine.error.title', 'Error'),
    subtitle: t(
      i18nLocale,
      'viewpointBuilder:routine.error.execute.subtitle',
      'Load failed'
    ),
    text: supportMessage ? [userMessage, supportMessage] : userMessage,
  });
  logError(error);
};

const mergeViewSettings = (
  base: ViewpointSavedViewSettings | null | undefined,
  overrides: ViewpointSavedViewSettings | undefined
): ViewpointSavedViewSettings | null | undefined => {
  if (!overrides) return base;
  const merged: ViewpointSavedViewSettings = { ...(base ?? {}) };
  for (const [viewId, override] of Object.entries(overrides)) {
    const key = viewId as keyof ViewpointSavedViewSettings;
    merged[key] = { ...(merged[key] ?? {}), ...override };
  }
  return merged;
};

export const openViewpoint = ({
  viewpoint,
  startContext,
  isFirstOpenedTraversal,
  clearExistingData = true,
  context,
  viewSettingsOverrides,
  groupBysOverride,
}: OpenViewpointArgs) => {
  const viewpointWithOverrides: APIViewpointAttributes = {
    ...viewpoint,
    viewSettings: mergeViewSettings(
      viewpoint.viewSettings,
      viewSettingsOverrides
    ),
    groupBys: groupBysOverride ?? viewpoint.groupBys,
  };
  return openTraversalInVisualization({
    executeViewpointConfig: {
      ...viewpointWithOverrides,
      ...viewpointToTraversalBase(viewpointWithOverrides),
      componentSelection: getComponentSelection(startContext),
      viewpointId: viewpointWithOverrides._id,
      type: 'EXECUTE_SAVED_VIEWPOINT',
      groupBys: viewpointWithOverrides.groupBys ?? [],
      conditionalFormatting: viewpointWithOverrides.conditionalFormatting ?? [],
      context,
      clearCurrentState: clearExistingData,
    },
    traversal: viewpointWithOverrides,
    isFirstOpenedTraversal,
  });
};

type OpenTraversalInVisualizationArgs = {
  executeViewpointConfig: ExecuteViewpointPayload;
  traversal: APIViewpointAttributes | null;
  isFirstOpenedTraversal: boolean;
};

export const openTraversalInVisualization = async ({
  executeViewpointConfig,
  traversal,
  isFirstOpenedTraversal,
}: OpenTraversalInVisualizationArgs) => {
  dispatchAction(componentGraphSelectionChanged({ graphSelection: [] }));
  dispatchAction(viewpointLoadingStatusChanged(true));
  // i18nLocale$.state OK here: loader title set once on dispatch; the loader UI is closed before locale could meaningfully change
  const i18nLocale = i18nLocale$.state;
  dispatchAction(
    setIsLoadingWithConfig({
      title: t(
        i18nLocale,
        'viewpointBuilder:loading.dataSet',
        'Loading data set...'
      ),
    })
  );

  const isExecuteSavedViewpoint = isExecuteSavedViewpointPayload(
    executeViewpointConfig
  );
  const viewpointId = isExecuteSavedViewpoint
    ? executeViewpointConfig.viewpointId
    : undefined;
  const params = loadedStateOperations.fromExecuteViewpointPayload(
    executeViewpointConfig,
    viewpointId
  );

  const selectedModule = await firstValueFrom(
    selectedModule$.pipe(map(state => state.selectedModule))
  );

  try {
    const componentIdsAndReferencesOrError = await buildTraversalState(
      params.data
    );
    if (isError(componentIdsAndReferencesOrError)) {
      dispatchAction(setIsLoading(false));
      dispatchAction(viewpointLoadingStatusChanged(false));
      if (
        componentIdsAndReferencesOrError.reason !== TraversalErrorReason.ABORTED
      ) {
        showTraversalExecutionError(
          componentIdsAndReferencesOrError.ardoqError
        );
      }
      return false;
    }

    dispatchAction(
      scopeDataLoaded({
        scopeData: componentIdsAndReferencesOrError.scopeData,
      })
    );
    dispatchAction(enterViewpointMode());

    if (selectedModule !== AppModules.WORKSPACES) {
      dispatchAction(
        requestShowAppModule({
          selectedModule: AppModules.WORKSPACES,
        })
      );
    }

    const loadedState = loadedStateOperations.attachLoadedGraph(
      params,
      componentIdsAndReferencesOrError
    );
    const traversalViewId = getSupportedTraversalViewIdOrDefault(
      executeViewpointConfig.viewName
    );
    const { clearCurrentState = false, context } = executeViewpointConfig;
    const { groupBys, conditionalFormatting } = getGroupsAndFormatting(
      traversal,
      executeViewpointConfig
    );
    const viewSettings =
      traversal?.viewSettings ??
      (isExecuteSavedViewpoint ? null : executeViewpointConfig.viewSettings) ??
      null;
    const isCanvasPrototype = hasFeature(Features.CANVAS_PROTOTYPE);
    const shouldApplyViewpointConfigs = getShouldApplyViewpointConfigs({
      isFirstOpenedTraversal,
      isCanvasPrototype: isDiscoverJourney() || isCanvasPrototype,
      context,
    });
    const isDatasetEmpty =
      !componentIdsAndReferencesOrError.componentIdsAndReferences.componentIds
        .length;

    if (
      shouldApplyViewpointConfigs &&
      (isDiscoverJourney() || isCanvasPrototype)
    ) {
      setApplicationViewState({
        conditionalFormatting,
        groupBys,
        loadedStates: [loadedState],
        viewNameCandidate: traversalViewId,
        viewSettings,
      });
    } else {
      dispatchUpdateLoadedState({
        loadedState,
        clearCurrentState,
        executeViewpointConfig,
        viewName: shouldApplyViewpointConfigs
          ? viewIdToUserFriendlyName[toRenderedViewpointViewId(traversalViewId)]
          : undefined,
      });
      // Remove after the KFQ12026 release is out.
      if (shouldApplyViewpointConfigs) {
        dispatchAction(
          setActiveMainTabLeft({
            activeTabId: toRenderedViewpointViewId(traversalViewId),
            isViewpointMode: true,
          })
        );
        applyViewpointViewSettings(
          traversal ?? { viewName: traversalViewId, viewSettings }
        );
        applyGroupingRules({ groupingRules: groupBys });
        applyFormattingAndLabelRules({
          rules: conditionalFormatting,
        });
      }
    }

    if (
      shouldApplyViewpointConfigs &&
      hasFeature(Features.CANVAS_PROTOTYPE_EDGE)
    ) {
      updateContextSwitcher(traversal, loadedState);
    }

    if (
      isDiscoverJourney() &&
      context === 'VIEWPOINT_SELECTED_ON_COMPONENT_PAGE' &&
      !isDatasetEmpty
    ) {
      contextToolBarCommands.toggleDetailsPanel();
      const startSet =
        componentIdsAndReferencesOrError.componentIdsAndReferences
          .startSetResult;
      dispatchAction(
        componentGraphSelectionChanged({ graphSelection: startSet })
      );
    }
    dispatchAction(setIsLoading(false));
    dispatchAction(viewpointLoadingStatusChanged(false));
    return true;
  } catch (e) {
    logError(Error('Loading subgraph from traversal config failed'));
    dispatchAction(setIsLoading(false));
    dispatchAction(viewpointLoadingStatusChanged(false));
    return false;
  }
};

const getGroupsAndFormatting = (
  traversal: APIViewpointAttributes | null,
  executeViewpointConfig: ExecuteViewpointPayload
) => {
  const groupBys = traversal?.groupBys ?? executeViewpointConfig.groupBys ?? [];
  const conditionalFormatting =
    traversal?.conditionalFormatting ??
    executeViewpointConfig.conditionalFormatting ??
    [];

  return {
    groupBys,
    conditionalFormatting,
  };
};

const updateContextSwitcher = (
  traversal: APIViewpointAttributes | null,
  loadedState: LoadedState | null
) => {
  const startType =
    traversal?.startType ??
    (loadedState
      ? loadedStateOperations.getStartContextComponentType(loadedState)
      : null);

  if (!startType) return;

  const query = getTypeQuery(startType);
  dispatchAction(
    setCanvasSidebarQuery({
      query,
    })
  );
};

type GetShouldApplyViewpointConfigsArgs = {
  isFirstOpenedTraversal: boolean;
  isCanvasPrototype: boolean;
  context: ExecuteViewpointPayload['context'];
};

const getShouldApplyViewpointConfigs = ({
  isFirstOpenedTraversal,
  isCanvasPrototype,
  context,
}: GetShouldApplyViewpointConfigsArgs) => {
  // In multi-block mode only apply viewpoint configurations for the first
  // opened traversal if the viewpoint is a saved viewpoint.
  if (isFirstOpenedTraversal) {
    return true;
  }

  if (!isCanvasPrototype && !hasFeature(Features.COMPONENT_OVERVIEW_2025)) {
    return false;
  }
  // In the canvas prototype also an unsaved viewpoint can have viewpoint
  // configurations, like grouping and formatting, e.g. the default viewpoint
  // used in the component page.
  return (
    context === 'VIEWPOINT_SELECTED' ||
    context === 'VIEWPOINT_SELECTED_ON_COMPONENT_PAGE' ||
    context === 'VIEWPOINT_DEEP_LINK'
  );
};

type UpdateLoadedStateParams = {
  loadedState: LoadedState;
  clearCurrentState: boolean;
  executeViewpointConfig: ExecuteViewpointPayload;
  viewName?: string;
};

const dispatchUpdateLoadedState = ({
  loadedState,
  clearCurrentState,
  executeViewpointConfig,
  viewName,
}: UpdateLoadedStateParams) => {
  if (clearCurrentState) {
    dispatchAction(
      clearAndSetLoadedState({ loadedStates: [loadedState], viewName })
    );
    return;
  }

  if (isLoadedStateHashString(executeViewpointConfig.loadedStateHash)) {
    dispatchAction(
      loadedStateUpdated({
        loadedState,
        existingHash: executeViewpointConfig.loadedStateHash,
      })
    );
    return;
  }
  dispatchAction(
    registerLoadedState({ loadedStates: [loadedState], viewName })
  );
};
